import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SingleTextAreaFieldFormModalData } from 'src/app/interface/components/forms/single-text-area-field-form.interface';

/** Plain-text length for Quill HTML (aligned with `app-text-area-field` counter). */
function htmlToPlainText(html: string): string {
  if (!html || !String(html).trim()) {
    return '';
  }
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent ?? '';
}

function maxPlainCharactersValidator(max: number, valueFormat: 'html' | 'plain'): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = String(control.value ?? '');
    if (!raw) {
      return null;
    }
    const plain = valueFormat === 'html' ? htmlToPlainText(raw) : raw;
    if (plain.length <= max) {
      return null;
    }
    return { maxlength: { requiredLength: max, actualLength: plain.length } };
  };
}

@Component({
  selector: 'common-single-text-area-field-form-modal',
  templateUrl: './single-text-area-field-form-modal.component.html',
  styleUrls: ['./single-text-area-field-form-modal.component.scss'],
})
export class SingleTextAreaFieldFormModalComponent implements OnInit {
  @Input() title: string = '';
  @Input() inputLabel: string = '';
  @Input() currentValue: string = '';
  @Input() placeholder: string = '';
  @Input() minHeightPx = 120;
  @Input() maxCharacters: number | null = 500;
  @Input() valueFormat: 'html' | 'plain' = 'plain';
  @Input() required = true;
  @Input() labelButtonCancel: string = 'maia.doors.button.cancel';
  @Input() labelButtonSave: string = 'maia.doors.button.save';
  @Input() loading: boolean = false;

  @Output() save = new EventEmitter<string>();
  @Output() cancel = new EventEmitter<void>();

  form: FormGroup;
  textControl: FormControl;

  constructor(
    private fb: FormBuilder,
    private matDialogRef: MatDialogRef<SingleTextAreaFieldFormModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SingleTextAreaFieldFormModalData,
  ) {
    if (this.data) {
      this.title = this.data.title || this.title;
      this.inputLabel = this.data.inputLabel || this.inputLabel;
      this.currentValue = this.data.currentValue || this.currentValue;
      this.placeholder = this.data.placeholder || this.placeholder;
      this.minHeightPx = this.data.minHeightPx ?? this.minHeightPx;
      this.maxCharacters = this.data.maxCharacters ?? this.maxCharacters;
      this.valueFormat = this.data.valueFormat ?? this.valueFormat;
      this.required = this.data.required ?? this.required;
      this.labelButtonCancel = this.data.labelButtonCancel || this.labelButtonCancel;
      this.labelButtonSave = this.data.labelButtonSave || this.labelButtonSave;
    }

    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.maxCharacters != null) {
      validators.push(maxPlainCharactersValidator(this.maxCharacters, this.valueFormat));
    }

    this.textControl = new FormControl(this.currentValue, validators);
    this.form = this.fb.group({
      text: this.textControl,
    });
  }

  ngOnInit(): void {
    if (this.currentValue) {
      this.textControl.setValue(this.currentValue);
    }
  }

  onEventClose(): void {
    this.matDialogRef.close();
  }

  onEventCancel(): void {
    this.cancel.emit();
    this.matDialogRef.close();
  }

  onEventSave(): void {
    if (this.form.valid) {
      const value = (this.textControl.value ?? '').toString();
      this.save.emit(value);
      this.matDialogRef.close(value);
    }
  }
}

